import axios from 'axios'

export const EnvironmentName = {
  unknown: 'unknown',
  local: 'local',
  dev: 'dev',
  staging: 'staging',
  production: 'production',
} as const

export type EnvironmentName = (typeof EnvironmentName)[keyof typeof EnvironmentName]

/**
 * Return the environment that this app is running in.
 */
export function resolveEnvironment(): EnvironmentName {
  // TODO: Use this everywhere we need to resolve the environment, to ensure
  // that we're using the same algorithm.

  // Use the hostname from the browser location bar.
  const browserHostname = window.location.host
  const hostParts = browserHostname.split(':')[0].split('.');
  let hostSubdomain = hostParts[0]
  // A special case - if the first element of the hostname is 'checkout', we should
  // use the second part of the hostname to determine the running environment
  if (hostSubdomain === 'checkout') {
    hostSubdomain = hostParts[1]
    if (['shofur', 'gogocharters', 'nationalbuscharter', 'bus'].includes(hostSubdomain)) {
      return EnvironmentName.production
    }
  }

  if (hostSubdomain === 'localhost') {
    return EnvironmentName.local
  } else if (hostSubdomain === 'dev') {
    return EnvironmentName.dev
  } else if (hostSubdomain === 'auto') {
    return EnvironmentName.staging
  } else if (hostSubdomain === 'app') {
    return EnvironmentName.production
  } else {
    // Couldn't determine the environment!
    console.error("Could not determine environment name from browser location " + browserHostname)
    return EnvironmentName.unknown
  }
}

/**
 * Returns the hostname for the public PDF service for the current environment.
 */
export function pdfAppHost(): string {
  const env = resolveEnvironment();
  switch(env) {
    case EnvironmentName.local:
    case EnvironmentName.dev:
      return "pdf.dev.coachrail.com";
    case EnvironmentName.staging:
      return "pdf.auto.coachrail.com";
    case EnvironmentName.production:
      return "pdf.app.coachrail.com";
    default:
      return "pdf.app.coachrail.com";
  }
}

/**
 * Generates a string for the metadata title and environment indicator in non-production environments
 * @returns The prefix if not in production, otherwise an empty string
 */
export function envTitlePrefix(): string {
  const env = resolveEnvironment()
  return env === EnvironmentName.local ||
  env === EnvironmentName.dev ||
  env === EnvironmentName.staging
    ? `${env.toUpperCase()} `
    : ''
}

/**
 * Generates a prefixed host name if a prefix is provided.
 * @param prefix The optional prefix to prepend to the host name.
 * @returns The host name with prefix if provided, otherwise an empty string.
 */
export const hostPrefix = (prefix?: string): string => (prefix ? `${prefix}.` : '')

// Set up a global property for saveQuote if in development mode
declare global {
  interface Window {
    saveQuote?: boolean
    google: any
  }
}

if (process.env.NODE_ENV === 'development') {
  // Only want to save quotes from CI right now while in dev mode.
  window.saveQuote = true
}

/**
 * Returns the base URL for the internal API.
 *
 * @returns The base URL.
 */
export const baseUrl = (): string => {
  return '/api'
}

/**
 * Returns the base URL to reference shared static content for the current environment.
 */
export const staticContentPrefix = (): string => {
    // The local development environment is not typically set up to serve
    // shared static user content from it's EFS drive/S3 bucket, so we defer
    // to using the shared dev instance. In all other cases, load the content
    // from the origin of this application.
    if(resolveEnvironment() == EnvironmentName.local) {
        return "https://dev.coachrail.com"
    } else {
        return "";
    }
}

/**
 * Determines the environment prefix based on the current host.
 * @returns The environment prefix based on the current host.
 */
export const environmentPrefix = (): string => {
  let host = window.location.host

  if (host.includes('localhost')) {
    return 'dev'
  }
  let hasHttpTest = /^((http|https|ftp):\/\/)/
  if (hasHttpTest.test(host)) {
    host = host.split('/').find((string) => {
      return string.includes('coachrail')
    }) || host
  }
  let split = host.split('.')
  if (split[0] === 'checkout') {
    return split[1]
  }
  return split[0]
}


/**
 * Provides a split key based on the subdomain of the current host.
 * @returns A specific key string based on the subdomain.
 */
export const splitKey = (): string => {
  const hostSubdomain = getHostSubdomain()

  if (hostSubdomain === 'localhost' || hostSubdomain === 'dev') {
    return 'q65ir5g1n7pek8dn6f7rm4n0ua6patbsv69p'
  } else if (hostSubdomain === 'auto') {
    return 'q17bli4sobmq2riile3p8vm6vjdg9rkkcgr4'
  } else if (hostSubdomain === 'app') {
    return '4mfo5hbqg0cbkifhoocbtj26jukglmlbf1o9'
  }

  return ''

}

/**
 * Provides the Auth0 config based on the subdomain of the current host.
 * @returns The Auth0 config based on the subdomain.
 */
export const auth0Config = (): { domain: string, clientId: string } => {
  const hostSubdomain = getHostSubdomain()

  if (hostSubdomain === 'localhost' || hostSubdomain === 'dev') {
    return {
      domain: 'charterup-dev.us.auth0.com',
      clientId: '6EVt5ZRMhXXNdJYBGUbP352SvBTFfQmB'
    }
  } else if (hostSubdomain === 'auto') {
    // TODO: update this to the correct config when we have it
    return {
      domain: 'charterup-staging.us.auth0.com',
      clientId: 'YFeEkfwzC6jhfvqVSOH4ddRVFOn9ogOm'
    }  } else if (hostSubdomain === 'app') {
    // TODO: update this to the correct config when we have it
    return {
      domain: 'charterup.us.auth0.com',
      clientId: 'tE2lIV5B6FsdO9LP61n7FJXNCzx5s5in'
    }  }

  return {
    domain: '',
    clientId: ''
  }
}

/**
 * Provides the Finix config based on the subdomain of the current host.
 * @returns an environment of either "sandbox" or "live" and and applicationId
 */
export const finixConfig = (): { environment: 'sandbox' | 'live', applicationId: string } => {
  const hostSubdomain = getHostSubdomain()

  if (hostSubdomain === 'localhost' || hostSubdomain === 'dev') {
    return {
      environment: "sandbox",
      applicationId: "APigGDxtKyaBfwqMAoM2RVRB"
    }
  } else if (hostSubdomain === 'auto') {
    return {
      environment: "sandbox",
      applicationId: "APigGDxtKyaBfwqMAoM2RVRB"
    }
  } else if (hostSubdomain === 'app') {
    return {
      environment: "live",
      applicationId: "APtkkr768DDXcQttsm2pahvz"
    }
  }

  return null
}

const getHostSubdomain = (): string => {
  let hostSubdomain = window.location.host.split(':')[0].split('.')[0]
  if (hostSubdomain === 'checkout') {
    hostSubdomain = window.location.host.split(':')[0].split('.')[1]
    if (['shofur', 'gogocharters', 'nationalbuscharter', 'bus'].includes(hostSubdomain)) {
      hostSubdomain = 'app'
    }
  }
  return hostSubdomain
}

/**
 * Returns true if the environment is production.
 */
export const isProductionEnvironment = (): boolean => {
  const env = resolveEnvironment()
  return env === EnvironmentName.production
}
